import { createContext } from "react";
import type { Role } from "@/shared/types/role";

export interface SessionUser {
  readonly uid: string;
  readonly email: string | null;
  readonly displayName: string | null;
  readonly role: Role | null;
  readonly mfaEnrolled: boolean;
  /**
   * Permission strings from `GET /api/auth/me` (v2.0 A8). Drives every
   * permission-gated surface via `hasPermission` / `usePermission`. Empty when
   * the API predates the field, so gated UI stays hidden (fail closed).
   */
  readonly permissions: readonly string[];
}

/**
 * Why a Firebase-authenticated user can still be denied entry. A valid
 * Firebase token is necessary but NOT sufficient: the user must also exist
 * in the backend DB. `null` means "no rejection" (normal logged-out or
 * authenticated state).
 *
 * - `not-provisioned`: the backend rejected the identity (401/403/404 from
 *   `/api/auth/me`). The Firebase user has no corresponding account — we sign
 *   them out and surface a "contact support" message. Retrying won't help.
 * - `verification-failed`: we could not reach the backend to confirm the
 *   account (network error or 5xx). We do NOT grant access (the original bug
 *   was letting unverified users in), but we keep the Firebase session so the
 *   operator can retry without re-entering credentials.
 */
export type AuthRejection = "not-provisioned" | "verification-failed";

export interface AuthContextValue {
  readonly status: "loading" | "authenticated" | "unauthenticated";
  readonly user: SessionUser | null;
  /**
   * Set only when a Firebase session was established but backend verification
   * denied or could not confirm access. `null` in every normal state.
   */
  readonly rejection: AuthRejection | null;
  readonly signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);
